import type {
  CreateDemoTagResponse,
  HelperProfileResponse,
  LeaderboardResponse,
  OwnerReportsResponse,
  ReportInput,
  ReportResponse,
  TagStatusResponse,
} from '@notlost/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`HTTP ${status}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { ...init, headers: { 'content-type': 'application/json', ...init?.headers } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error);
  }
  return (await res.json()) as T;
}

export function getTagStatus(tagId: string): Promise<TagStatusResponse> {
  return request(`/api/tags/${encodeURIComponent(tagId)}`);
}

export function sendReport(tagId: string, report: ReportInput, helperId: string | null): Promise<ReportResponse> {
  return request(`/api/tags/${encodeURIComponent(tagId)}/reports`, {
    method: 'POST',
    body: JSON.stringify(report),
    headers: helperId ? { 'x-helper-id': helperId } : undefined,
  });
}

export function getOwnerReports(tagId: string, ownerKey: string): Promise<OwnerReportsResponse> {
  return request(`/api/owner/${encodeURIComponent(tagId)}/reports`, { headers: { 'x-owner-key': ownerKey } });
}

export function markReturned(tagId: string, ownerKey: string, reportId: string): Promise<{ ok: true; returnedAt: string | null }> {
  return request(`/api/owner/${encodeURIComponent(tagId)}/reports/${encodeURIComponent(reportId)}/returned`, {
    method: 'POST',
    headers: { 'x-owner-key': ownerKey },
  });
}

export function createDemoTag(label: string, notifyEmail: string): Promise<CreateDemoTagResponse> {
  return request('/api/demo/tags', {
    method: 'POST',
    body: JSON.stringify({ label: label.trim() || undefined, notifyEmail: notifyEmail.trim() || undefined }),
  });
}

export function getHelper(helperId: string): Promise<HelperProfileResponse> {
  return request('/api/helper', { headers: { 'x-helper-id': helperId } });
}

export function setNickname(helperId: string, nickname: string): Promise<HelperProfileResponse> {
  return request('/api/helper/nickname', {
    method: 'PUT',
    headers: { 'x-helper-id': helperId },
    body: JSON.stringify({ nickname }),
  });
}

export function getLeaderboard(): Promise<LeaderboardResponse> {
  return request('/api/leaderboard');
}
